/* eslint-disable react/no-danger */
import {
  Input,
  Stack,
  IconButton,
  Button,
  Typography,
  Avatar,
  Box,
  Divider,
  CircularProgress,
  Chip,
} from '@mui/joy';
import React, { useEffect, useMemo, useState } from 'react';
import { IoIosClose, IoIosSearch, IoIosShuffle } from 'react-icons/io';
import {
  platformDetails,
  Project,
  rank,
} from 'assets/Projects';
// import getProjects from 'utils/Api';
// import { staticProjectsData } from './staticProjectsData';
import moment from 'moment';
import { FiCode, FiFile, FiPlay } from 'react-icons/fi';
import { CiSearch, CiWifiOff } from 'react-icons/ci';
import { useMobileMode, useTabletMode } from 'components/Responsive';
import { GoDownload } from 'react-icons/go';
import { useSearchParams, useNavigate } from 'react-router-dom';
import getProjects from './staticProjectsData';

function Message({
  children,
  title,
  subtitle,
}: {
  children: JSX.Element;
  title: string;
  subtitle: string;
}) {
  const mobile = useMobileMode();

  return (
    <Stack
      direction="row"
      justifyContent="center"
      alignItems="center"
      flexWrap="wrap"
      gap={mobile ? 3 : 5}
      padding={mobile ? '2rem 3rem' : 15}
      sx={mobile ? (theme) => ({
        background: theme.palette.background.body,
        width: '100vw',
        marginX: '-1rem',
        zIndex: 1,
      }) : undefined}
    >
      {children}
      <Stack direction="column" gap={0.5} maxWidth="25rem" textAlign={mobile ? 'center' : 'left'}>
        <Typography
          level="h3"
          fontSize="clamp(1.2rem, 5vw, 1.875rem)"
        >
          {title}
        </Typography>
        <Typography level="body2">
          {subtitle}
        </Typography>
      </Stack>
    </Stack>
  );
}

function ProjectCard({
  project,
}: {
  project: Project;
}) {
  const [pressed, setPressed] = useState(false);
  const navigate = useNavigate();

  const mobile = useMobileMode();

  const handleClick = () => {
    const projectUrl = `/projects/${project.title.toLowerCase().replace(/\s+/g, '-')}`;
    navigate(projectUrl);
  };

  const footer = useMemo(
    () => ((project.createdAt || project.updatedAt) ? `project published to ${
      platformDetails[project.platform].label}${project.createdAt ? ` ${moment(project.createdAt).fromNow()}` : ''
    }${project.updatedAt ? `, updated ${moment(project.updatedAt).fromNow()}` : ''
    }` : ''),
    [project],
  );

  return (
    <Stack
      id={project.title}
      direction={mobile ? 'column' : 'row'}
      padding={2}
      gap={2}
      onClick={handleClick}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      className={`${pressed ? 'pressed' : ''}`}
      alignItems="stretch"
      flexWrap="wrap"
      sx={(theme) => ({
        willChange: 'filter',
        transition: 'all ease .2s',
        position: 'relative',
        cursor: 'pointer',
        borderRadius: '1rem',
        backgroundColor: theme.palette.background.body,
        border: '1px solid transparent',
        overflow: 'hidden',
        '&:hover, &.pressed': {
          zIndex: 1,
          border: `1px solid ${theme.palette.text.primary}`,
          filter: `drop-shadow(0 .4rem 0 ${theme.palette.text.primary})`,
          transform: 'translateY(-.4rem)',
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: '-.4rem',
            left: 0,
            width: '100%',
            height: '.4rem',
            backgroundColor: 'transparent',
          },
          '& .illustration': {
            opacity: 0.8,
          },

          '& .icon': {
            backgroundColor: theme.palette.text.primary,
            color: theme.palette.background.level1,
          },

          '& + hr': {
            display: 'none',
          },
        },
        '&.pressed': {
          filter: `drop-shadow(0 .2rem 0 ${theme.palette.text.primary})`,
          transform: 'translateY(-.2rem)',
        },
      })}
    >
      <Stack
        direction={mobile ? 'row' : 'column'}
        gap={mobile ? 2 : 1}
      >
        <Avatar
          component="span"
          className="icon"
          variant="plain"
          size="md"
          sx={{ borderRadius: '.5rem', transition: 'all ease .2s', zIndex: 1 }}
        >
          {platformDetails[project.platform].icon({ size: '1.3rem' })}
        </Avatar>
      </Stack>

      <Stack
        zIndex={1}
        gap={0.5}
        flex={1}
        sx={{
          minWidth: 'min(100%, 20rem)',
        }}
      >
        <Typography level="h3" alignItems="baseline">
          {project.title}
          <Typography textColor="text.tertiary" fontWeight="300" marginLeft={1}>
            {platformDetails[project.platform].sublabel.toLocaleLowerCase()}
          </Typography>
        </Typography>
        <Typography
          level="body2"
          sx={{
            flex: 1,
            '& p': {
              margin: 0,
            },
          }}
        >
          <text
            dangerouslySetInnerHTML={{ __html: project.description || '' }}
          />
          <text>
            {project.description}
          </text>
        </Typography>
        {project.skills && project.skills.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.5} marginY={1}>
            {project.skills.map((skill) => (
              <Chip
                key={skill}
                size="sm"
                variant="outlined"
                color="neutral"
                sx={(theme) => ({
                  fontSize: '0.75rem',
                  paddingX: '0.5rem',
                  height: '1.5rem',
                  backgroundColor: theme.palette.background.level1,
                  borderColor: theme.palette.divider,
                })}
              >
                {skill}
              </Chip>
            ))}
          </Stack>
        )}
        <Typography level="body3" marginTop={1}>
          {project.language ? (
            <>
              <Typography textColor="text.primary">
                {project.language}
              </Typography>
              {' '}
            </>
          ) : ''}
          {footer}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        zIndex={1}
        gap={1}
        alignItems="start"
        marginTop={mobile ? '.5rem' : 'initial'}
        justifyContent={mobile ? 'end' : 'initial'}
        width={mobile ? '100%' : 'initial'}
      >
        {project.source && (
          <Button
            component="a"
            href={project.source}
            target="_blank"
            size={mobile ? 'md' : 'sm'}
            color="neutral"
            variant="outlined"
            sx={(theme) => ({
              transition: 'all ease .2s, height ease .4s',
              borderRadius: '.5rem',
              width: 'fit-content',
              flexShrink: 0,
              padding: '1 2',
              background: theme.palette.background.body,

              '&:hover': {
                background: theme.palette.text.primary,
                color: theme.palette.background.level1,
                borderColor: theme.palette.text.primary,
              },
              '&:active': {
                transform: 'scale(.98)',
              },
            })}
            startDecorator={project.language !== undefined ? (<FiCode />) : (<FiFile />)}
          >
            {project.language !== undefined ? 'Explore source' : 'Show project'}
          </Button>
        )}
        {project.demo && (
          <Button
            component="a"
            href={project.demo}
            target="_blank"
            size={mobile ? 'md' : 'sm'}
            color="neutral"
            variant="outlined"
            sx={(theme) => ({
              transition: 'all ease .2s',
              borderRadius: '.5rem',
              width: 'fit-content',
              flexShrink: 0,
              padding: '1 2',

              borderColor: theme.palette.text.primary,
              background: theme.palette.text.primary,
              color: theme.palette.background.level1,
              '&:hover': {
                backgroundColor: theme.palette.background.body,
                color: theme.palette.text.primary,
              },
              '&:active': {
                transform: 'scale(.98)',
              },
            })}
            startDecorator={<FiPlay />}
          >
            Run demo
          </Button>
        )}
      </Stack>
      {project.thumbnail && (
        <Box
          component="img"
          src={project.thumbnail}
          alt={project.title}
          className="illustration"
          sx={{
            transition: 'all ease .2s',
            position: 'absolute',
            minHeight: '100%',
            minWidth: '61%',
            left: '40%',
            top: '50%',
            WebkitMaskImage:
              'linear-gradient(to left,black 10%,transparent 80%)',
            maskImage: 'linear-gradient(to left,black 10%,transparent 80%)',
            opacity: 0,
            filter: 'blur(5px)',
            transform: 'translateY(-50%)',
          }}
        />
      )}
    </Stack>
  );
}

export default function Directory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const mobile = useMobileMode();
  const tablet = useTabletMode();

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [search, setSearch] = useState(searchParams.get('search') || '');

  const [projects, setProjects] = useState([] as Project[]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();

  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);

  useEffect(() => {
    setLoading(true);
    // setProjects(staticProjectsData);
    getProjects().then((data) => {
      setProjects(data);
    }).catch(() => {
      setError(new Error('There was an error loading the projects. Please try again later.'));
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    setSearchParams(params);
  }, [search, setSearchParams]);

  // Update the debounced search term
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timeout);
  }, [search]);

  // Filtering
  useEffect(() => {
    setFilteredProjects(projects.filter(
      (project) => (debouncedSearch === ''
        || project.title.toLowerCase().includes(debouncedSearch.toLowerCase())
        || project.platform.toLowerCase().includes(debouncedSearch.toLowerCase())
        || project.description?.toLowerCase().includes(debouncedSearch.toLowerCase())
        || project.language?.toLowerCase().includes(debouncedSearch.toLowerCase())
      ),
    ));
  }, [projects, debouncedSearch]);

  // Pick a random project and navigate to it
  const randomize = () => {
    const randomIndex = Math.floor(Math.random() * filteredProjects.length);
    const randomProject = filteredProjects[randomIndex];
    const projectUrl = `/projects/${randomProject.title.toLowerCase().replace(/\s+/g, '-')}`;
    navigate(projectUrl);
  };

  const focusInput = () => {
    const inputElement = document.getElementById('search');
    if (inputElement) {
      const { top } = inputElement.getBoundingClientRect();
      window.scrollTo({
        top: top - 16,
        behavior: 'smooth',
      });
    }
  };

  // When F is pressed, focus the search input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'f') {
        e.preventDefault();
        document.getElementById('search')?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <Stack paddingY={1} width="100%">
      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Input
          size="lg"
          id="search"
          placeholder={`Search ${filteredProjects.length} project${filteredProjects.length === 1 ? '' : 's'}`}
          variant="outlined"
          value={search}
          className={debouncedSearch !== search ? 'loading' : ''}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={mobile ? focusInput : () => { }}
          startDecorator={debouncedSearch !== search ? (
            <CircularProgress
              size="sm"
              color="neutral"
              variant="soft"
              sx={{
                margin: '-4px',
                '& circle': {
                  transition: 'all ease .05s',
                },
              }}
            />
          ) : (<IoIosSearch />)}
          endDecorator={search !== '' && (
            <IconButton
              variant="plain"
              color="neutral"
              sx={{ fontSize: '1.5rem' }}
              onClick={() => setSearch('')}
            >
              <IoIosClose />
            </IconButton>
          )}
          sx={(theme) => ({
            transition: 'all ease .2s',
            width: (mobile || tablet) ? '100%' : 'min(100%, 30rem)',
            cursor: 'pointer',
            borderRadius: '0',
            outline: 'none',
            backgroundColor: theme.palette.background.body,
            '--Input-focusedHighlight': theme.palette.text.secondary,

            '&:before': {
              transition: 'all ease .2s',
            },

            '&:has(:focus), &.loading': {
              borderRadius: (mobile || tablet) ? '100vmax' : '100vmax 0 0 100vmax',
            },
          })}
        />
        <Button
          size="lg"
          variant="outlined"
          color="neutral"
          startDecorator={<IoIosShuffle style={{ fontSize: '1.5rem' }} />}
          onClick={randomize}
          sx={(theme) => ({
            transition: 'all ease .2s',
            position: 'relative',
            borderRadius: '0',
            width: 'fit-content',
            flexShrink: 0,
            paddingInline: '1rem',

            borderColor: theme.palette.text.primary,
            background: theme.palette.text.primary,
            color: theme.palette.background.level1,
            '& > span > svg': {
              transition: 'all ease .2s',
            },
            '&:hover': {
              backgroundColor: theme.palette.background.body,
              color: theme.palette.text.primary,
              '& > span > svg': {
                transform: 'rotate3d(1, 0, 0, 540deg) scale(1.2)',
              },
            },
            '&:active': {
              transform: 'scale(.98)',
            },
          })}
        >
          Randomize
        </Button>
      </Stack>
      <Stack paddingBlockStart={4}>
        {loading ? (
          <Message title="Working on it..." subtitle="We are fetching the projects for you. This should only take a few seconds.">
            <CircularProgress size="lg" color="neutral" variant="outlined">
              <GoDownload size="1.5rem" />
            </CircularProgress>
          </Message>
        ) : filteredProjects
          .sort((a, b) => rank(b, filteredProjects) - rank(a, filteredProjects))
          .map((project, index) => (
            <React.Fragment
              key={`${project.platform}-${project.title}-${project.source}`}
            >
              <ProjectCard
                project={project}
              />
              {index < filteredProjects.length - 1 && (
                <Divider />
              )}
            </React.Fragment>
          ))}
        {filteredProjects.length === 0 && !loading && debouncedSearch === search && (
          <Message title={error ? 'This usually never happens...' : 'Well that\'s embarrassing...'} subtitle={error ? error.message : 'We couldn\'t find any projects matching your search criteria. Try a different search term.'}>
            {error ? <CiWifiOff size="5rem" /> : <CiSearch size="5rem" />}
          </Message>
        )}
      </Stack>
    </Stack>
  );
}
